/**
 * Normalize theme list fields from fetchThemeData: API may return arrays or JSON-encoded strings.
 * (Admin themeupdate uses the same logic as ensureArray.)
 */
export function ensureThemeJsonArray(val) {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Encode a single path segment for S3/CDN URLs.
 * Spaces, parentheses (e.g. "file (1).png"), and other reserved chars become %XX.
 * Avoids URL API pathname setter, which can leave `()` unencoded and break staging CDNs.
 */
function encodePathSegment(seg) {
  if (!seg) return seg;
  let decoded = seg;
  try {
    decoded = decodeURIComponent(seg);
  } catch {
    decoded = seg;
  }
  return encodeURIComponent(decoded);
}

function encodePathSegments(relativePath) {
  return String(relativePath)
    .split("/")
    .map(encodePathSegment)
    .join("/");
}

/**
 * Build a safe URL for theme images/audio (S3 keys under REACT_APP_S3_PATH, or full https URLs).
 * Encodes each path segment so spaces, parentheses, %, etc. resolve to the correct object key.
 */
export function getDvSourceImageUrl(imgPath) {
  if (!imgPath) return "";
  const s = String(imgPath).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const origin = u.origin;
      const encodedPath = encodePathSegments(u.pathname.replace(/^\/+/, ""));
      const query = u.search || "";
      const hash = u.hash || "";
      return `${origin}/${encodedPath}${query}${hash}`;
    } catch {
      // encodeURI leaves () alone — encodeURIComponent each segment instead.
      const m = s.match(/^(https?:\/\/[^/]+)(\/.*)?$/i);
      if (!m) return s;
      const pathAndRest = m[2] || "";
      const qIndex = pathAndRest.search(/[?#]/);
      const pathOnly = qIndex >= 0 ? pathAndRest.slice(0, qIndex) : pathAndRest;
      const rest = qIndex >= 0 ? pathAndRest.slice(qIndex) : "";
      return `${m[1]}/${encodePathSegments(pathOnly.replace(/^\/+/, ""))}${rest}`;
    }
  }
  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/?$/, "/");
  const path = s.replace(/^\/+/, "");
  return `${base}${encodePathSegments(path)}`;
}

/**
 * Value for CSS `background-image` / `url(...)`. Unquoted `url(https://.../a(2).png)` is invalid — the `(` ends the URL.
 */
export function toCssUrlValue(href) {
  if (!href) return "none";
  const escaped = String(href).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

/**
 * Pick `background_mob` vs `background_desk` from theme data.
 * Portrait mobile art must not be used in landscape: many phones stay ≤768px wide when rotated,
 * so width alone would stretch tall portrait assets across a wide viewport.
 */
export function themeBackgroundKeyForViewport(themeData) {
  if (!themeData || typeof themeData !== "object") return null;
  const w = typeof window !== "undefined" ? window.innerWidth : 0;
  const narrow = w > 0 && w <= 768;
  const portrait =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: portrait)").matches;
  const mob = themeData.background_mob;
  const desk = themeData.background_desk;
  if (narrow && portrait) return mob || desk || null;
  return desk || mob || null;
}

const STAGE_BACKGROUND_KEYS = {
  1: ["stage1_background", "stage_1_background", "wordpuzzle_background"],
  2: ["stage2_background", "stage_2_background", "unscramble_background"],
  3: ["stage3_background", "stage_3_background", "quiz_background"],
  4: ["stage4_background", "stage_4_background", "image_quiz_background"],
  5: ["stage5_background", "stage_5_background", "image_only_background"],
};

/** Per-stage background from theme admin, with global desk/mob fallback. */
export function themeBackgroundKeyForStage(themeData, stageNumber) {
  if (!themeData || typeof themeData !== "object") return null;
  const keys = STAGE_BACKGROUND_KEYS[stageNumber] || [];
  for (const key of keys) {
    const val = themeData[key];
    if (val != null && String(val).trim() !== "") return String(val).trim();
  }
  return themeBackgroundKeyForViewport(themeData);
}

export function parseStageNumberFromPath(pathname) {
  const path = String(pathname || "");
  if (/^\/game(?:\/|$)/.test(path)) return 2;
  const m = path.match(/^\/stage(\d)(?:\/|$)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 && n <= 5 ? n : null;
}

/** Theme text color for stage titles, subtitles, and related copy. */
export function getLandingPageTitleColor(themeData) {
  const raw =
    themeData?.landing_page_title_color ?? themeData?.landing_page_title_colour;
  if (raw == null || raw === "") return undefined;
  const c = String(raw).trim();
  return c || undefined;
}

const NAMED_HEX_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

/** Normalize admin/user color fields to #rrggbb for gradients and CSS vars. */
export function normalizeThemeHexColor(val) {
  if (val == null || val === "") return null;
  const v = String(val).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_HEX_COLORS[v.toLowerCase()];
  return named || null;
}

export function darkenThemeHexColor(hex, amount = 0.18) {
  const normalized = normalizeThemeHexColor(hex);
  if (!normalized) return null;
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const scale = (channel) =>
    Math.max(0, Math.min(255, Math.round(channel * (1 - amount))));
  const toHex = (channel) => channel.toString(16).padStart(2, "0");
  return `#${toHex(scale(r))}${toHex(scale(g))}${toHex(scale(b))}`;
}

/** Flat button color for stage HUD buttons (timer, ?, skip, submit, heading). */
export function getThemeButtonGradient(themeData) {
  const start = normalizeThemeHexColor(themeData?.button_color);
  return { start, end: start };
}

/** Theme text color for HUD labels (timer, points, heading, submit, rules). */
export function getThemeButtonTextColor(themeData) {
  const raw =
    themeData?.button_Textcolor ?? themeData?.button_text_color;
  if (raw == null || String(raw).trim() === "") return null;
  return normalizeThemeHexColor(raw) || String(raw).trim();
}

/** Publish button gradient + text CSS variables for all game stages. */
export function applyThemeButtonGradientStyles(themeData) {
  const root = document.documentElement;
  const { start, end } = getThemeButtonGradient(themeData);
  const textColor = getThemeButtonTextColor(themeData);
  if (start && end) {
    root.style.setProperty("--theme-button-start", start);
    root.style.setProperty("--theme-button-end", end);
  } else {
    root.style.removeProperty("--theme-button-start");
    root.style.removeProperty("--theme-button-end");
  }
  if (textColor) {
    root.style.setProperty("--theme-button-text", textColor);
  } else {
    root.style.removeProperty("--theme-button-text");
  }
}

/** Theme color for answer/input boxes (admin "Input box color"). */
export function getThemeInputBoxColor(themeData) {
  return normalizeThemeHexColor(themeData?.inputBox_color);
}

/** Publish the input box color CSS variable for all game stages. */
export function applyThemeInputBoxColorStyle(themeData) {
  const root = document.documentElement;
  const color = getThemeInputBoxColor(themeData);
  if (color) {
    root.style.setProperty("--theme-input-box-color", color);
  } else {
    root.style.removeProperty("--theme-input-box-color");
  }
}
